import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiWithAuth } from "../../services/utils";
import { campaignService } from "../../services/main";
import {
  Breadcrumb,
  Button,
  Card,
  Label,
  Modal,
  Spinner,
  Table,
  TextInput,
  Badge,
} from "flowbite-react";
import {
  HiHome,
  HiPlus,
  HiRefresh,
  HiSearch,
  HiTrash,
  HiPencil,
  HiPaperAirplane,
  HiClipboardCopy,
  HiChartBar,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiEye,
} from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import toast from "react-hot-toast";

type Campaign = {
  id: number;
  name: string;
  description?: string | null;
  status: string;
  sendType: string;
  targetType?: string;
  scheduledAt?: string | null;
  createdAt: string;
  segment?: {
    id: number;
    name: string;
  } | null;
  _count?: {
    deliveryRecords: number;
  };
};

export const CampaignListNew = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const totalCount = campaigns.length;
  const draftCount = campaigns.filter((c) => c.status === "DRAFT").length;
  const scheduledCount = campaigns.filter((c) => c.status === "SCHEDULED").length;
  const activeCount = campaigns.filter((c) => c.status === "ACTIVE" || c.status === "SENDING").length;
  const completedCount = campaigns.filter((c) => c.status === "COMPLETED").length;

  const fetchCampaigns = useCallback(async (isRefresh = false) => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      toast.error("Authentication required. Please login again.");
      return;
    }

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;

      const response = await campaignService.listCampaigns(params);
      if (response.data && Array.isArray(response.data.campaigns || response.data)) {
        const campaignsArray = Array.isArray(response.data.campaigns) 
          ? response.data.campaigns 
          : response.data;
        setCampaigns(campaignsArray);
        if (isRefresh) {
          toast.success("Campaigns refreshed successfully");
        }
      } else {
        setCampaigns([]);
      }
    } catch (error: any) {
      console.error("Error fetching campaigns:", error);
      toast.error(error.response?.data?.message || "Failed to fetch campaigns");
      setCampaigns([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleDuplicate = async (campaignId: number) => {
    const token = sessionStorage.getItem("token");
    if (!token) return;
    try {
      const response = await campaignService.duplicateCampaign(campaignId);
      if (response.data) {
        toast.success("Campaign duplicated successfully");
        void fetchCampaigns();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to duplicate campaign");
    }
  };

  const handleSend = async (campaignId: number) => {
    const token = sessionStorage.getItem("token");
    if (!token) return;
    if (!confirm("Are you sure you want to send this campaign now?")) return;
    try {
      const response = await campaignService.sendCampaign(campaignId);
      if (response.data) {
        toast.success("Campaign is being sent now");
        setTimeout(() => {
          void fetchCampaigns(true);
        }, 1000);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to send campaign");
    }
  };

  const handleDelete = async (campaignId: number) => {
    const token = sessionStorage.getItem("token");
    if (!token) return;
    if (!confirm("Are you sure you want to delete this campaign?")) return;
    try {
      await campaignService.deleteCampaign(campaignId);
      toast.success("Campaign deleted successfully");
      void fetchCampaigns();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete campaign");
    }
  };

  const filtered = campaigns.filter((campaign) => {
    const haystack = `${campaign.name} ${campaign.description || ""} ${campaign.status}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      DRAFT: { color: "gray", label: "Draft" },
      SCHEDULED: { color: "info", label: "Scheduled" },
      SENDING: { color: "warning", label: "Sending" },
      ACTIVE: { color: "success", label: "Active" },
      COMPLETED: { color: "success", label: "Completed" },
    };
    const config = statusConfig[status] || { color: "gray", label: status };
    return (
      <Badge 
        color={config.color as any} 
        className="font-semibold inline-flex items-center"
      >
        {config.label}
      </Badge>
    );
  };

  const getSendTypeBadge = (sendType: string) => {
    const typeConfig: Record<string, { color: string; label: string }> = {
      NOW: { color: "success", label: "Send Now" },
      SCHEDULED: { color: "info", label: "Scheduled" },
      RECURRING: { color: "warning", label: "Recurring" },
      AUTOMATION: { color: "purple", label: "Automation" },
    };
    const config = typeConfig[sendType] || { color: "gray", label: sendType };
    return (
      <Badge 
        color={config.color as any} 
        className="font-semibold inline-flex items-center"
      >
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="px-4 pt-6 pb-8">
      {/* Header */}
      <div className="mb-6">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item onClick={() => navigate("/")}>
            <div className="flex items-center gap-x-3 cursor-pointer">
              <HiHome className="text-xl" />
              <span className="dark:text-white">Home</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Campaigns</Breadcrumb.Item>
        </Breadcrumb>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3 mb-2">
              <HiChartBar className="text-blue-600 dark:text-blue-400" />
              Campaign Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create, manage, and track your marketing campaigns efficiently
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              color="gray"
              onClick={() => fetchCampaigns(true)}
              disabled={refreshing || loading}
              className="font-medium"
            >
              <HiRefresh className={`text-lg mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              color="primary"
              onClick={() => navigate("/pages/campaigns/new")}
              className="font-medium"
            >
              <HiPlus className="text-lg mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Campaigns</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-lg">
              <HiChartBar className="text-2xl text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="bg-gray-50 dark:bg-gray-900/20 border-l-4 border-l-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Drafts</p>
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{draftCount}</p>
            </div>
            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <HiPencil className="text-2xl text-gray-600 dark:text-gray-400" />
            </div>
          </div>
        </Card>

        <Card className="bg-cyan-50 dark:bg-cyan-900/20 border-l-4 border-l-cyan-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Scheduled</p>
              <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{scheduledCount}</p>
            </div>
            <div className="p-3 bg-cyan-100 dark:bg-cyan-800 rounded-lg">
              <HiClock className="text-2xl text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </Card>

        <Card className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-l-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Active</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{activeCount}</p>
            </div>
            <div className="p-3 bg-amber-100 dark:bg-amber-800 rounded-lg">
              <HiPaperAirplane className="text-2xl text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </Card>

        <Card className="bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Completed</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{completedCount}</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-800 rounded-lg">
              <HiCheckCircle className="text-2xl text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="w-full sm:w-auto flex-1">
            <TextInput
              icon={HiSearch}
              placeholder="Search campaigns by name, description, or status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              className="block w-full sm:w-auto rounded-lg border border-gray-300 bg-white dark:bg-gray-800 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:text-white"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="SENDING">Sending</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
            </select>
            {statusFilter && (
              <Button
                color="gray"
                size="sm"
                onClick={() => {
                  setStatusFilter("");
                  setPage(1);
                }}
              >
                <HiXCircle className="text-lg" />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Campaigns Table */}
      {loading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading campaigns...</span>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <HiChartBar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No campaigns</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {search || statusFilter
                ? "Try adjusting your filters"
                : "Get started by creating a new campaign"}
            </p>
            {!search && !statusFilter && (
              <div className="mt-6 flex justify-center">
                <Button color="primary" onClick={() => navigate("/pages/campaigns/new")}>
                  <HiPlus className="mr-2" />
                  Create Campaign
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <>
          <Card className="border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-full">
                <Table.Head className="bg-gray-50 dark:bg-gray-800/50 border-b-2 border-gray-200 dark:border-gray-700">
                  <Table.HeadCell className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Campaign Name
                  </Table.HeadCell>
                  <Table.HeadCell className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </Table.HeadCell>
                  <Table.HeadCell className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Send Type
                  </Table.HeadCell>
                  <Table.HeadCell className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Target
                  </Table.HeadCell>
                  <Table.HeadCell className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Segment
                  </Table.HeadCell>
                  <Table.HeadCell className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </Table.HeadCell>
                  <Table.HeadCell className="px-6 py-4 text-center text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {paged.map((campaign, index) => (
                    <Table.Row 
                      key={campaign.id} 
                      className={`bg-white dark:bg-gray-800 hover:bg-blue-50/50 dark:hover:bg-gray-700/50 transition-all duration-150 cursor-pointer border-b border-gray-100 dark:border-gray-700 ${
                        index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/30 dark:bg-gray-800/50'
                      }`}
                      onClick={() => navigate(`/pages/campaigns/${campaign.id}`)}
                    >
                      <Table.Cell className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                            <HiChartBar className="text-white text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white text-base">
                              {campaign.name}
                            </div>
                            {campaign.description && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md mt-0.5">
                                {campaign.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        <div className="flex items-center">
                          {getStatusBadge(campaign.status)}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        <div className="flex items-center">
                          {getSendTypeBadge(campaign.sendType)}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap">
                        <span className="text-gray-900 dark:text-white font-medium">
                          {campaign.targetType?.replace(/_/g, " ") || "N/A"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap">
                        <span className="text-gray-700 dark:text-gray-300">
                          {campaign.segment?.name || "All Contacts"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <HiClock className="text-sm" />
                          <span className="text-sm">
                            {new Date(campaign.createdAt).toLocaleDateString("en-US", { 
                              month: "short", 
                              day: "numeric", 
                              year: "numeric" 
                            })}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            color="gray"
                            onClick={() => navigate(`/pages/campaigns/${campaign.id}`)}
                            className="px-3 py-2.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:border-blue-300 transition-all rounded-lg min-w-[40px] h-[40px] flex items-center justify-center"
                            title="View Details"
                          >
                            <HiEye className="text-lg" />
                          </Button>
                          {campaign.status === "DRAFT" && (
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => handleSend(campaign.id)}
                              className="px-3 py-2.5 hover:bg-green-700 transition-all rounded-lg min-w-[40px] h-[40px] flex items-center justify-center"
                              title="Send Campaign"
                            >
                              <HiPaperAirplane className="text-lg" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            color="gray"
                            onClick={() => handleDuplicate(campaign.id)}
                            className="px-3 py-2.5 hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:border-purple-300 transition-all rounded-lg min-w-[40px] h-[40px] flex items-center justify-center"
                            title="Duplicate Campaign"
                          >
                            <HiClipboardCopy className="text-lg" />
                          </Button>
                          <Button
                            size="sm"
                            color="failure"
                            onClick={() => handleDelete(campaign.id)}
                            className="px-3 py-2.5 hover:bg-red-700 transition-all rounded-lg min-w-[40px] h-[40px] flex items-center justify-center"
                            title="Delete Campaign"
                          >
                            <HiTrash className="text-lg" />
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <Card className="mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-400">
                  Showing <span className="font-bold text-gray-900 dark:text-white">{(page - 1) * pageSize + 1}</span> to{" "}
                  <span className="font-bold text-gray-900 dark:text-white">{Math.min(page * pageSize, filtered.length)}</span> of{" "}
                  <span className="font-bold text-gray-900 dark:text-white">{filtered.length}</span> campaigns
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    color="gray"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="disabled:opacity-50"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                          page === i + 1
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <Button
                    color="gray"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="disabled:opacity-50"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
